import React from 'react';
import {
  Box,
  Skeleton,
  SkeletonCircle,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import { ListItemText, TableList } from 'app/components/ChakraTableList';
import InfiniteScroll from 'react-infinite-scroll-component';
import { fetchNotificationsEffect } from 'app/store/Notifications/effects';
import { getNotificationsNextLink } from 'app/store/Notifications/selectors';
import { getAvatarImage } from 'app/utils/utils';
import { useInfiniteLoader } from 'app/utils/hooks';
import { Notification } from 'types/notification.types';
import { ChakraAvatar } from 'app/components/Core';
import { rowHeights } from 'app/components/ChakraTableList/constants';
import { NotificationsListRowRenderer } from 'app/components/Notifications/NotificationsListRowRenderer';
import { breakpoints } from 'app/chakra-theme/foundations/breakpoints';
import { UnreadDot } from './UnreadDot';
import { ParseMessage } from '../ParseMessage';
import { getRowData, onRowClick } from './utils';

export interface NotificationsListProps {
  notifications: Notification[];
  markNotificationAsRead: (guid: string) => void;
  markNotificationAsOld: (guid: string) => void;
  onClick?: (notification: Notification) => void;
}

export const NotificationsList: React.FC<NotificationsListProps> = ({
  notifications,
  markNotificationAsOld,
  markNotificationAsRead,
  onClick,
}) => {
  const [, infiniteScrollProps, loadingMoreNotifications] = useInfiniteLoader({
    dataLength: notifications.length,
    nextLinkSelector: getNotificationsNextLink,
    fetchNext: (link) => fetchNotificationsEffect({ next: link }),
  });
  const [isMobileView] = useMediaQuery(`(max-width: ${breakpoints.lg})`);

  return (
    <>
      {notifications.length > 0 && (
        <Text mt={6} mb={4} fontSize="md" fontWeight={500}>
          Recent
        </Text>
      )}
      <InfiniteScroll {...infiniteScrollProps}>
        <TableList
          regularRowHeight={
            isMobileView ? rowHeights.higher : rowHeights.regular
          }
          onRowClick={({ rowData }) =>
            onRowClick({
              notification: rowData.notification,
              markNotificationAsRead,
              onClick,
            })
          }
          rowRenderer={(props) => {
            const notification = props.row.rowData as Notification;
            return (
              <NotificationsListRowRenderer
                notification={notification}
                markNotificationAsOld={markNotificationAsOld}
                rowRendererProps={props}
              />
            );
          }}
          loadingOptions={{
            loading: false,
            loadingMore: loadingMoreNotifications,
            repeat: 3,
            loader: {
              accessory: <SkeletonCircle width="40px" height="40px" />,
              title: <Skeleton width="350px" height="24px" />,
            },
          }}
          columns={[
            { key: 'accessory', name: '', accessory: true },
            { key: 'title', name: '' },
            { key: 'action', name: '', width: 32 },
          ]}
          rows={notifications.map((notification) => {
            const {
              rowData,
              isInteractive,
              initials,
              profileImages,
              avatarColors,
            } = getRowData(notification);

            return {
              rowData,
              ...(isInteractive ? { to: notification.rel } : {}),
              accessory: (
                <ChakraAvatar
                  size="md"
                  initials={initials}
                  src={getAvatarImage(profileImages)}
                  colors={avatarColors}
                />
              ),
              title: (
                <ListItemText
                  data-testid="notification-item-text"
                  primaryTextProps={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-all',
                    as: Box,
                  }}
                  primary={
                    <ParseMessage
                      message={notification}
                      isMobileView={isMobileView}
                    />
                  }
                />
              ),
              action: <UnreadDot isHidden={notification.read} />,
            };
          })}
        />
      </InfiniteScroll>
    </>
  );
};
